import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Skeleton, SkeletonGroup } from '@/components/ui/Skeleton';
import { GoalsChat } from '@/components/profile/goals/GoalsChat';
import { ObjectivesBoard } from '@/components/profile/goals/ObjectivesBoard';
import { useWorkspaceObjectives } from '@/lib/workspace-objectives';
import {
  applyProposal,
  inApplyOrder,
  type GoalsConversation,
  loadGoals,
  resolveGoals,
  runGoalsTurn,
  stopGoalsTurn,
} from '@/lib/company-goals';

const GoalsSkeleton = () => (
  <SkeletonGroup label="Chargement des objectifs" className="grid gap-6 lg:grid-cols-[minmax(340px,420px)_1fr]">
    <Skeleton className="h-[60vh] rounded-xl" />
    <div className="space-y-2">
      <Skeleton className="h-40 w-full rounded-xl" />
      {Array.from({ length: 3 }, (_, index) => <Skeleton key={index} className="h-28 w-full rounded-xl" />)}
    </div>
  </SkeletonGroup>
);

/**
 * Step ② of the qualification (13/10/2026): the first time, a page that says
 * what it is for; then the analyst's chat next to the table of objectives. The
 * analyst proposes a first version from the qualification, explains it, and
 * revises it when challenged; each proposal is written once validated.
 */
export const GoalsStep = () => {
  const { objectives, members, loading, save, setStatus } = useWorkspaceObjectives();
  const [conversation, setConversation] = useState<GoalsConversation | null>(null);
  const [running, setRunning] = useState(false);
  const [live, setLive] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const loaded = await loadGoals();
      setConversation(loaded);
      setRunning(loaded.turnRunning);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Conversation indisponible.');
      setConversation({ messages: [], proposals: [], turnRunning: false });
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const send = async (text: string | null) => {
    setError(null); setRunning(true); setLive('');
    if (text) setConversation((current) => current && { ...current, messages: [...current.messages, { id: `local-${Date.now()}`, role: 'user', content: text, createdAt: new Date().toISOString() }] });
    try {
      const outcome = await runGoalsTurn(text, (delta) => setLive((current) => current + delta));
      setConversation((current) => ({ turnRunning: false, ...current, ...outcome }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'La réponse n’a pas abouti.');
      await reload();
    } finally {
      setRunning(false); setLive('');
    }
  };

  const resolve = async (ids: string[], outcome: 'applied' | 'dismissed') => {
    if (!conversation) return;
    setBusy(true); setError(null);
    const done: string[] = [];
    const created: Record<string, string> = {};
    try {
      if (outcome === 'applied') {
        for (const proposal of inApplyOrder(conversation.proposals.filter((entry) => ids.includes(entry.id)))) {
          created[proposal.id] = await applyProposal(proposal, objectives, created, { save, setStatus });
          done.push(proposal.id);
        }
      } else done.push(...ids);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'L’objectif n’a pas été enregistré.');
    }
    try {
      if (done.length) {
        const next = await resolveGoals(done, outcome, created);
        setConversation((current) => current && { ...current, ...next });
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'La liste n’a pas été mise à jour : rechargez la page.');
    } finally {
      setBusy(false);
    }
  };

  if (!conversation || loading) return <GoalsSkeleton />;
  const hasActive = objectives.some((objective) => objective.status === 'active');
  const fresh = conversation.messages.length === 0 && conversation.proposals.length === 0 && !running;
  if (fresh && !hasActive) {
    return (
      <section className="mx-auto max-w-2xl py-16 text-center">
        <h2 className="font-display text-3xl font-semibold leading-tight text-domino-ink">Les objectifs que Domino suit avec vous</h2>
        <p className="mt-4 text-base leading-7 text-domino-inkSoft">
          L’analyste part de la qualification pour proposer vos objectifs : le résultat qui compte, les leviers qui le produisent,
          leur cible, leur échéance et leur criticité. Vous les discutez avec lui, puis vous validez ceux que Domino suivra chaque jour.
        </p>
        <Button className="mt-8" onClick={() => void send(null)}>Proposer mes objectifs</Button>
      </section>
    );
  }
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(340px,420px)_1fr]">
      <div className="lg:sticky lg:top-4 lg:h-[calc(100vh-9rem)] lg:self-start">
        <GoalsChat messages={conversation.messages} running={running} live={live} error={error} onSend={(text) => void send(text)} onStop={() => void stopGoalsTurn().catch(() => undefined)} />
      </div>
      <div className="min-w-0 space-y-4">
        {running && conversation.proposals.length === 0 && !hasActive
          ? <SkeletonGroup label="Préparation des objectifs" className="space-y-2"><Skeleton className="h-40 w-full rounded-xl" />{Array.from({ length: 3 }, (_, index) => <Skeleton key={index} className="h-28 w-full rounded-xl" />)}</SkeletonGroup>
          : <ObjectivesBoard objectives={objectives} proposals={conversation.proposals} members={members} busy={busy || running} onApply={(ids) => void resolve(ids, 'applied')} onDismiss={(ids) => void resolve(ids, 'dismissed')} />}
      </div>
    </div>
  );
};
